<template>
  <div class="container">
    <!-- Header -->
    <header class="header">
      <h1>Go-Dutch</h1>
      <p>Split bills fairly, instantly</p>
    </header>

    <!-- Upload Section -->
    <section class="upload-section" :class="{ processing: isProcessing }">
      <button @click="openFile" :disabled="isProcessing" class="upload-btn">
        <span v-if="!isProcessing">Upload Receipt</span>
        <span v-else>Processing…</span>
      </button>

     <input
      ref="fileInput"
      type="file"
      accept="image/*,.heic,.heif,.webp"
      @change="handleFileUpload"
      hidden
    />


      <p class="info-text">
        Supported: JPEG, PNG, HEIC, WebP • Max 10MB
      </p>

      <div v-if="error" class="error-banner">
        {{ error }}
      </div>
    </section>

    <!-- Preview -->
    <div v-if="previewUrl" class="preview">
      <img :src="previewUrl" alt="Receipt" />
    </div>

    <!-- Items -->
    <section v-if="items.length" class="items-section">
      <h2>Receipt Items</h2>

      <ul class="items-list">
        <li v-for="(item, i) in items" :key="i" class="item-card">
          <div class="item-info">
            <div class="description">{{ item.description }}</div>
            <div class="price-details">
              {{ item.quantity }} × {{ currencyFormatter.format(item.unitPrice) }}
              → <strong>{{ currencyFormatter.format(item.price) }}</strong>
            </div>
          </div>

          <div class="assign-users">
          <div
            v-for="person in people"
            :key="person"
            class="person-chip"
          >
            <span>{{ person }}</span>

            <select
              v-model.number="item.assignments[person]"
              @change="validateAssignments"
            >
              <option
                v-for="n in allocationOptions(item, person)"
                :key="n"
                :value="n"
              >
                {{ n }}
              </option>
            </select>
          </div>
        </div>
        </li>
      </ul>

     <!-- Totals -->
<div class="totals-card">
  <div class="total-line">
    <span>Subtotal</span>
    <strong>{{ currencyFormatter.format(total) }}</strong>
  </div>
  <div class="total-line tip">
    <span>Tip (10%)</span>
    <strong>{{ currencyFormatter.format(tipAmount) }}</strong>
  </div>
  <div class="total-line grand">
    <span>Total</span>
    <strong>{{ currencyFormatter.format(totalWithTip) }}</strong>
  </div>
</div> <!-- ✅ THIS WAS MISSING -->

<!-- Add person Modal -->
<div
  v-if="items.length && (showAddPersonPanel || people.length === 0)"
  class="modal-overlay"
>
  <div class="add-person-modal">
    <div class="panel-header">
      <h3>Add people</h3>
      <button class="close-btn" @click="closeAddPerson" :disabled="people.length === 0">
        Done
      </button>
    </div>

    <div class="add-person">
      <input
        v-model="newPerson"
        placeholder="Add person (e.g. Alex)"
        @keyup.enter="addPerson"
        autofocus
      />
      <button @click="addPerson" :disabled="!newPerson.trim()">
        Add
      </button>
    </div>

    <div v-if="people.length" class="people-preview">
      <span
        v-for="person in people"
        :key="person"
        class="person-pill"
      >
        {{ person }}
      </span>
    </div>
  </div>
</div>



      <!-- Summary -->
      <div v-if="people.length" class="summary-section">
        <h2>Split Summary</h2>

        <div class="summary-grid">
          <div v-for="person in people" :key="person" class="person-card">
            <div class="person-name">{{ person }}</div>
            <div class="person-amount">
            {{ currencyFormatter.format(formattedSplits[person]) }}
          </div>
          </div>
        </div>

        <div v-if="unassignedItemsTotal > 0" class="unassigned-total">
          Unassigned items: {{ currencyFormatter.format(unassignedItemsTotal) }}
          → {{ currencyFormatter.format(unassignedSplitPerPerson) }} each
        </div>
      </div>
    </section>
  </div>
</template>

<script>

const CURRENCY_LOOKUP = {
  "R":   { code: "ZAR", symbol: "R", locale: "en-ZA" },
  "€":   { code: "EUR", symbol: "€", locale: "fr-FR" },
  "$":   { code: "USD", symbol: "$", locale: "en-US" },
  "£":   { code: "GBP", symbol: "£", locale: "en-GB" },
  "CHF": { code: "CHF", symbol: "CHF", locale: "de-CH" },
  "¥":   { code: "JPY", symbol: "¥", locale: "ja-JP" }
};

import heic2any from "heic2any";

const debug = (...args) => {
  console.log("[GoDutch Upload]", ...args);
};


export default {
  name: "GoDutch",

  data() {
  return {
    items: [], 
    total: 0,
    people: [],
    newPerson: "",
    error: "",
    worker: null,
    isProcessing: false,
    previewUrl: "",
    tipPercentage: 0.1,

    showAddPersonPanel: false,

    // 👇 NEW
    currency: {
      code: "ZAR",
      symbol: "R",
      locale: "en-ZA",
    },
  };
},



  computed: {
  currencyFormatter() {
    return new Intl.NumberFormat(this.currency.locale, {
      style: "currency",
      currency: this.currency.code,
    });
  },

    tipAmount() {
      return this.total * this.tipPercentage;
    },

    totalWithTip() {
      return this.total + this.tipAmount;
    },

    splits() {
      const result = {};
      this.people.forEach(p => (result[p] = 0));

      // Assigned items
      this.items.forEach(item => {
        Object.entries(item.assignments).forEach(([person, qty]) => {
          result[person] += qty * item.unitPrice;
        });
      });

      // Tip split equally
      const tipShare = this.people.length
        ? this.tipAmount / this.people.length
        : 0;

      this.people.forEach(p => {
        result[p] += tipShare;
      });

      return result;
    },

    

    unassignedItemsTotal() {
      return this.items.reduce((sum, item) => {
        const assignedQty = Object.values(item.assignments).reduce(
          (s, q) => s + q,
          0
        );
        return sum + (item.quantity - assignedQty) * item.unitPrice;
      }, 0);
    },

    unassignedSplitPerPerson() {
      return this.people.length
        ? this.unassignedItemsTotal / this.people.length
        : 0;
    },

    formattedSplits() {
      return Object.fromEntries(
        this.people.map(p => [
          p,
          this.splits[p] + this.unassignedSplitPerPerson,
        ])
      );
    },
  },

  methods: {
    openFile() {
      debug("openFile() clicked");
      this.$refs.fileInput.click();
    },

    addPerson() {
  const name = this.newPerson.trim();
  if (!name || this.people.includes(name)) return;

  this.people.push(name);
  this.items.forEach(item => {
    if (!item.assignments) item.assignments = {};
    item.assignments[name] = 0;
  });
  this.newPerson = "";
},

closeAddPerson() {
  if (this.people.length === 0) {
    alert("Please add at least one person to split the bill.");
    return;
  }
  this.showAddPersonPanel = false;
},
allocationOptions(item, person) {
  // total assigned excluding this person
  const assignedToOthers = Object.entries(item.assignments)
    .filter(([p]) => p !== person)
    .reduce((sum, [, qty]) => sum + qty, 0);

  const currentValue = item.assignments[person] || 0;

  // max this person can select
  const maxAllowed = item.quantity - assignedToOthers + currentValue;

  // build dropdown options [0..maxAllowed]
  return Array.from({ length: maxAllowed + 1 }, (_, i) => i);
},

    validateAssignments() {
  for (const item of this.items) {
    const totalAssigned = Object.values(item.assignments).reduce(
      (s, q) => s + q,
      0
    );

    if (totalAssigned > item.quantity) {
      this.error = `Too many assignments for "${item.description}"`;
      return false;
    }
  }

  this.error = "";
  return true;
},


    cleanupWorker() {
      if (this.worker) {
        this.worker.terminate();
        this.worker = null;
      }
      this.isProcessing = false;
    },

    async handleFileUpload(e) {
      debug("Input value:", e.target.value);
      debug("Input files length:", e.target.files?.length);
      debug("handleFileUpload fired");
      debug("Event:", e);
      debug("Files list:", e.target.files);

      const file = e.target.files[0];
       if (!file) {
        debug("❌ No file returned from input (user likely cancelled)");
        return;
      }
      debug("✅ File received:", {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified,
      });

      if (this.worker) this.cleanupWorker();

     if (file.size > 10 * 1024 * 1024) {
          debug("❌ File too large:", file.size);
          this.error = "File too large (max 10MB)";
          return;
        }

        if (!file.type.startsWith("image/")) {
          debug("❌ File is not an image:", file.type);
        }

      this.isProcessing = true;
      this.error = "";
      this.items = [];
      this.total = 0;

      this.people = [];
      this.showAddPersonPanel = false;

      let processedFile = file;

        if (/\.(heic|heif)$/i.test(file.name) || file.type === "image/heic") {
          debug("📸 HEIC detected — converting");

          try {
            const blob = await heic2any({ blob: file, toType: "image/jpeg" });

            processedFile = new File([blob], "receipt.jpg", {
              type: "image/jpeg",
            });

            debug("✅ HEIC converted:", processedFile);
          } catch (err) {
            debug("❌ HEIC conversion failed", err);
            this.error = "Could not convert HEIC image";
            return;
          }
        }

          if (this.previewUrl) {
            URL.revokeObjectURL(this.previewUrl);
          }

          // Create new preview
          this.previewUrl = URL.createObjectURL(processedFile);
          debug("🖼 Preview URL created:", this.previewUrl);

      this.worker = new Worker(
        new URL("./tesseract-worker.js", import.meta.url),
        { type: "module" }
      );

      this.worker.onerror = (err) => {
        debug("❌ Worker error", err);
        this.error = "Image processing failed";
        this.cleanupWorker();
      };

      this.worker.onmessage = async ({ data }) => {
  debug("📨 Worker returned data", data);

  try {
    const res = await fetch(
      "https://godutch-production.up.railway.app/parse-bill",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_base64: data.base64 }),
      }
    );

    // ✅ handle non-200 responses
    if (!res.ok) {
      throw new Error(`Server error (${res.status})`);
    }

    const json = await res.json();

    // 💱 Currency handling
    if (json.currency_format) {
      const symbol = json.currency_format;

      this.currency =
        CURRENCY_LOOKUP[symbol] || {
          code: "ZAR",
          symbol,
          locale: "en-ZA",
        };
    }

    // 🧾 Items
    this.items = (json.items || []).map(raw => {
      const quantity = raw.quantity || 1;
      const price = raw.total_price || 0;
      const safeQuantity = quantity || 1;

      return {
        description: raw.description || "Item",
        quantity: safeQuantity,
        unitPrice: price / safeQuantity,
        price,
        assignments: Object.fromEntries(
          this.people.map(p => [p, 0])
        ),
      };
    });

    // 💰 Totals
    this.total = this.items.reduce((s, i) => s + i.price, 0);
    this.showAddPersonPanel = true;

  } catch (err) {
    console.error(err);
    this.error = err.message || "Failed to process receipt";
  } finally {
    this.cleanupWorker();

    if (this.$refs.fileInput) {
      this.$refs.fileInput.value = "";
      debug("🔁 File input reset");
    }
  }
};

debug("📤 Sending file to worker", processedFile);
this.worker.postMessage({ file: processedFile });

    },
  },

 beforeUnmount() {
  this.cleanupWorker();

    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
  },
};


</script>

<style scoped>
/* Base light mode (logo-inspired: orange accents + blue primaries) */
.container {
  max-width: 480px;
  margin: 0 auto;
  padding: 20px;
  min-height: 100vh;
  background: linear-gradient(135deg, #1d5fcc 0%, #df8417 100%); /* Blue to orange gradient for logo energy */
  color: #333;
  transition: all 0.4s ease;
  position: relative;
}

/* Dark mode (inverted for contrast: deep navy + warm amber) */
.container.dark {
  background: linear-gradient(135deg, #0d1b2a 0%, #b54708 100%); /* Navy blue to deep orange */
  color: #e0e0e0;
}

/* Global dark mode override */
html.dark-mode {
  color-scheme: dark;
}

/* Toggle button (subtle orange border) */
.dark-toggle {
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(255,255,255,0.2);
  color: white;
  border: 1px solid #df8417; /* Logo orange border */
  padding: 10px 16px;
  border-radius: 50px;
  font-size: 0.9rem;
  cursor: pointer;
  backdrop-filter: blur(10px);
  z-index: 100;
  transition: all 0.3s;
}
.dark-toggle:hover {
  background: rgba(223, 132, 23, 0.2); /* Subtle orange hover */
  transform: scale(1.05);
}

/* Dark mode adjustments */
.container.dark .upload-section,
.container.dark .items-section,
.container.dark .totals-card {
  background: #1e1e1e;
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);
}

.container.dark .item-card {
  background: #2d2d2d;
  border-left-color: #df8417; /* Orange accent in dark mode */
}

.container.dark .person-card {
  background: linear-gradient(135deg, #df8417 0%, #1d5fcc 100%); /* Orange to blue in dark */
}

.container.dark .unassigned-total {
  background: #df841733; /* Faded orange */
  color: #ffcc80;
}

.container.dark .preview img {
  box-shadow: 0 8px 25px rgba(0,0,0,0.6);
}

/* Header (white text on gradient) */
.header { 
  text-align: center; 
  color: white; 
  margin-bottom: 30px; 
}
.header h1 { 
  font-size: 3rem; 
  margin: 0; 
  font-weight: 800; 
  text-shadow: 0 2px 4px rgba(0,0,0,0.3); /* Subtle shadow for depth */
}
.header p { 
  opacity: 0.9; 
  font-size: 1.1rem; 
}

/* Upload section (clean white card with orange button) */
.upload-section {
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(29, 91, 204, 0.2); /* Blue-tinted shadow */
  text-align: center;
  margin-bottom: 20px;
}
.upload-btn { 
  background: #df8417; /* Logo orange */
  color: white; 
  border: none; 
  padding: 16px 32px; 
  font-size: 1.2rem; 
  border-radius: 50px; 
  cursor: pointer; 
  box-shadow: 0 8px 20px rgba(223, 132, 23, 0.4); 
  transition: all 0.3s; 
}
.upload-btn:hover:not(:disabled) { 
  transform: translateY(-3px); 
  box-shadow: 0 12px 25px rgba(223, 132, 23, 0.5); 
  background: #e68a00; /* Slightly brighter orange */
}
.info-text {
  margin-top: 12px;
  color: #666;
  font-size: 0.9rem;
}
.error-banner {
  margin-top: 15px;
  padding: 12px;
  background: #ffebee;
  color: #c62828;
  border-radius: 12px;
  font-weight: 500;
  border-left: 4px solid #df8417; /* Orange accent on error */
}

/* Preview (with blue border) */
.preview img {
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 16px;
  margin: 20px 0;
  box-shadow: 0 8px 25px rgba(29, 91, 204, 0.15);
  border: 2px solid #1d5fcc; /* Blue logo border */
}

/* Items section (white card with blue heading) */
.items-section {
  background: white;
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 10px 30px rgba(29, 91, 204, 0.15);
  margin-bottom: 20px;
}
.items-section h2 {
  text-align: center;
  color: #1d5fcc; /* Blue header */
  margin-top: 0;
}

.items-list {
  list-style: none;
  padding: 0;
  margin: 0 0 20px 0;
}
.item-card {
  background: #f8f9ff;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 12px;
  border-left: 5px solid #df8417; /* Orange left border */
}
.item-info .description {
  font-weight: 600;
  font-size: 1.05rem;
  color: #333;
}
.price-details {
  color: #1d5fcc; /* Blue for prices */
  font-size: 0.95rem;
  margin-top: 4px;
}
.assign-users {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.person-chip {
  background: #e3f2fd; /* Light blue */
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #1d5fcc; /* Blue border */
}
.person-chip input {
  margin: 0;
  accent-color: #df8417; /* Orange checkbox */
}

/* Totals card (orange background) */
.totals-card {
  background: #df8417;
  color: white;
  padding: 20px;
  border-radius: 16px;
  margin: 20px 0;
}
.total-line {
  display: flex;
  justify-content: space-between;
  font-size: 1.1rem;
  margin: 8px 0;
}
.total-line.tip { 
  opacity: 0.9; 
  color: rgba(255,255,255,0.9);
}
.total-line.grand {
  font-size: 1.4rem;
  font-weight: 800;
  padding-top: 12px;
  border-top: 2px solid rgba(255,255,255,0.3);
}
.add-person-panel {
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin: 20px 0;
  box-shadow: 0 10px 30px rgba(29, 91, 204, 0.15);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.panel-header h3 {
  margin: 0;
  color: #1d5fcc;
}

.close-btn {
  background: transparent;
  border: none;
  font-weight: bold;
  color: #df8417;
  cursor: pointer;
}

.people-preview {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.person-pill {
  background: #e3f2fd;
  border: 1px solid #1d5fcc;
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 0.85rem;
}

.reopen-add-person {
  width: 100%;
  margin-top: 10px;
  padding: 12px;
  border-radius: 12px;
  background: #1d5fcc;
  color: white;
  border: none;
  font-weight: bold;
}

/* Add person (green button with blue input) */
.add-person {
  display: flex;
  gap: 10px;
  margin: 20px 0;
}
.add-person input {
  flex: 1;
  padding: 14px;
  border: 2px solid #1d5fcc; /* Blue border */
  border-radius: 12px;
  font-size: 1rem;
  background: #f0f8ff; /* Light blue bg */
}
.add-person button {
  padding: 14px 24px;
  background: #4caf50; /* Keep green for "add" action */
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: bold;
}
/* Modal overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5); /* semi-transparent black */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

/* Modal content */
.add-person-modal {
  background: white;
  border-radius: 16px;
  padding: 30px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.3);
}


/* Summary section */
.summary-section h2 {
  text-align: center;
  color: #1d5fcc; /* Blue */
}
.summary-grid {
  display: grid;
  gap: 16px;
  margin: 20px 0;
}
.person-card {
  background: linear-gradient(135deg, #df8417 0%, #1d5fcc 100%); /* Orange to blue gradient */
  color: white;
  padding: 20px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 8px 20px rgba(223, 132, 23, 0.3);
}
.person-name {
  font-size: 1.3rem;
  font-weight: 700;
}
.person-amount {
  font-size: 2rem;
  font-weight: 800;
  margin: 8px 0;
}
.unassigned-note {
  font-size: 0.9rem;
  opacity: 0.9;
}
.final-amount {
  margin-top: 8px;
  font-size: 1.1rem;
}

.unassigned-total {
  background: #fff3e0;
  color: #df8417; /* Orange text */
  padding: 14px;
  border-radius: 12px;
  font-weight: 500;
  text-align: center;
  border-left: 4px solid #1d5fcc; /* Blue accent */
}

.person-chip select {
  border-radius: 8px;
  border: 1px solid #1d5fcc;
  padding: 4px 6px;
  font-size: 0.85rem;
  background: white;
}

/* Responsive tweaks */
@media (max-width: 480px) {
  .container { padding: 10px; }
  .header h1 { font-size: 2.5rem; }
  .upload-section, .items-section { padding: 20px; }
}
</style>